import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { PlanSlot } from "../data/api.ts";
import { paramSummary } from "../data/catalog.ts";
import { OPERATION_LABEL, OPERATOR_ROLE_LABEL, type Machine, type Operator, type TaskWithJob, type WorkingHours } from "../data/types.ts";
import { ACTIVE_STATUSES, alertsFor, isCompatible, optimize, runMinutes, sequence, setupKey, type PlanResult } from "../lib/optimizer.ts";
import { addDays, addWorkingMinutes, dayRanges, fmtDate, fmtDateTime, fmtDuration, fmtTime, nextWorkingInstant, startOfDay, startOfWeek, workingMinutesBetween } from "../lib/time.ts";
import { Badge, Check, Empty, ErrorBox, Loading, Modal, attempt, formatColor, navigate, toast, useApp, useData, useNow } from "../ui/kit.tsx";

const LABEL_W = 188;
const SNAP_MIN = 5;
type View = "jour" | "semaine";

const isActive = (t: TaskWithJob) => (ACTIVE_STATUSES as readonly string[]).includes(t.status);
const sameTime = (a: string | null, b: string | null) => (a ? new Date(a).getTime() : null) === (b ? new Date(b).getTime() : null);
export const unpublished = (t: TaskWithJob) => t.status !== "termine" && (t.pub_machine_id !== t.machine_id || !sameTime(t.pub_start, t.planned_start));

/** Fenêtre affichée pour une journée : de la première ouverture à la dernière fermeture. */
function dayWindow(hours: WorkingHours): [number, number] {
  const mins = hours.ranges.flatMap((r) => [r.start, r.end]).map((s) => { const [h, m] = s.split(":").map(Number); return h * 60 + (m || 0); });
  return mins.length ? [Math.min(...mins), Math.max(...mins)] : [7 * 60, 18 * 60];
}

export function PlanningPage() {
  const { api, org, can } = useApp();
  const edit = can("planning");
  const now = useNow(30_000);
  const [animate, setAnimate] = useState(false);
  const [view, setView] = useState<View>("jour");
  // après la fermeture, on montre directement la prochaine journée de travail
  const [anchor, setAnchor] = useState(() => startOfDay(nextWorkingInstant(new Date(), org.hours)));
  const [selected, setSelected] = useState<string | null>(null);
  const [plan, setPlan] = useState<PlanResult | null>(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const since = useMemo(() => addDays(startOfWeek(new Date()), -7), []);
  const { data, error, loading } = useData(api, async () => {
    const [machines, tasks, operators] = await Promise.all([api.machines(), api.tasks(since), api.operators()]);
    return { machines: machines.filter((m) => m.active), tasks: tasks.filter((t) => t.job.status !== "annule"), operators: operators.filter((o) => o.active) };
  }, [since], 15_000);
  const [local, setLocal] = useState<TaskWithJob[] | null>(null);
  useEffect(() => { setLocal(null); }, [data]);

  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Loading />;
  const machines = data.machines;
  const tasks = local || data.tasks;
  const hours = org.hours;

  if (!machines.length) {
    return (
      <div className="page">
        <Header title="Planning" />
        <Empty title="Commencez par décrire votre atelier">
          Ajoutez vos plieuses et encarteuses : le planning se construit machine par machine.
          {can("machines") && <div className="row-gap"><a className="btn btn-primary" href="#/parametres/machines">Ajouter mes machines</a></div>}
        </Empty>
      </div>
    );
  }

  const backlog = tasks.filter((t) => !t.machine_id && t.status !== "termine").sort((a, b) => a.job.due_at.localeCompare(b.job.due_at));
  const alerts = alertsFor(tasks, machines);
  const alertIds = new Set(alerts.map((a) => a.task_id));
  const toPublish = tasks.filter(unpublished).length;
  const days = view === "jour" ? [anchor] : Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i)).filter((d) => dayRanges(d, hours).length || hours.days.length === 0);

  const save = async (slots: PlanSlot[], okText?: string) => {
    // affichage immédiat, puis enregistrement
    const byId = new Map(slots.map((s) => [s.task_id, s]));
    setLocal(tasks.map((t) => {
      const s = byId.get(t.id);
      if (!s) return t;
      return { ...t, ...s, locked: s.locked ?? t.locked, status: t.status === "a_planifier" || t.status === "planifie" ? (s.machine_id ? "planifie" : "a_planifier") : t.status };
    }));
    await attempt(() => api.savePlan(slots), okText);
  };

  const runOptimize = () => setPlan(optimize({ machines, tasks, hours, now }));
  const applyPlan = async () => {
    if (!plan) return;
    setAnimate(true);
    window.setTimeout(() => setAnimate(false), 900);
    await save(plan.slots.map((s) => ({
      task_id: s.task_id, machine_id: s.machine_id, planned_start: s.start.toISOString(), planned_end: s.end.toISOString(),
      setup_min: s.setup_min, run_min: s.run_min,
    })), `Planning optimisé : ${plan.metrics.recalages} calage${plan.metrics.recalages > 1 ? "s" : ""}. Pensez à le publier.`);
    setPlan(null);
  };
  const publish = async () => {
    const n = await attempt(() => api.publishPlan());
    if (n !== undefined) toast(n ? `Planning publié : ${n} changement${n > 1 ? "s" : ""} envoyé${n > 1 ? "s" : ""} aux postes` : "Rien de nouveau à publier");
  };

  /** Calcule où atterrit une étape posée sur une machine à une heure donnée, et ce qui se décale derrière. */
  const computePlace = (t: TaskWithJob, m: Machine, at: Date) => {
    const lane = tasks.filter((x) => x.machine_id === m.id && x.id !== t.id && x.planned_start && x.status !== "termine")
      .sort((a, b) => a.planned_start!.localeCompare(b.planned_start!));
    const before = lane.filter((x) => new Date(x.planned_start!) < at);
    const prev = before[before.length - 1];
    let start = at;
    if (prev?.planned_end && new Date(prev.planned_end) > start) start = new Date(prev.planned_end);
    const after = lane.filter((x) => !before.includes(x));
    const movable = after.filter((x) => !x.locked && !isActive(x));
    const fixed = after.filter((x) => x.locked || isActive(x)).map((x) => ({ start: new Date(x.planned_start!), end: new Date(x.planned_end || x.planned_start!), key: setupKey(x) }));
    return sequence(m, [t, ...movable], start, prev ? setupKey(prev) : null, hours, fixed);
  };
  previewPlace = edit ? (t, machineId, at) => {
    const m = machines.find((x) => x.id === machineId);
    if (!m || isActive(t)) return null;
    const slot = computePlace(t, m, at).find((x) => x.task_id === t.id);
    return slot ? { start: slot.start, end: slot.end, setup: slot.setup_min, machine: m.name } : null;
  } : null;

  const place = (t: TaskWithJob, m: Machine | null, at: Date | null, opts: { lock?: boolean; manual?: number | null; okText?: string } = {}) => {
    if (!edit) { toast("Votre rôle ne permet pas de modifier le planning.", "err"); return; }
    if (isActive(t)) { toast("Ce job est en cours sur un poste : il ne peut pas être déplacé.", "err"); return; }
    if (!m || !at) {
      save([{ task_id: t.id, machine_id: null, planned_start: null, planned_end: null, setup_min: null, run_min: null, locked: false }], "Retiré du planning");
      return;
    }
    const task = opts.manual !== undefined ? { ...t, manual_run_min: opts.manual } : t;
    const slots = computePlace(task, m, at);
    if (!isCompatible(m, t)) toast(`${m.name} n'est pas paramétrée pour ce travail : vérifiez la machine.`, "err");
    const mine = slots.find((x) => x.task_id === t.id);
    save(slots.map((s) => ({
      task_id: s.task_id, machine_id: m.id, planned_start: s.start.toISOString(), planned_end: s.end.toISOString(),
      setup_min: s.setup_min, run_min: s.run_min,
      ...(s.task_id === t.id ? { locked: opts.lock ?? true, ...(opts.manual !== undefined ? { manual_run_min: opts.manual } : {}) } : {}),
    })), opts.okText ?? (mine ? `${t.job.of_number} sur ${m.name} : ${fmtTime(mine.start)} → ${fmtTime(mine.end)}` : undefined));
  };
  /** Nouvelle durée de production (minutes), null = revenir au calcul. Les jobs suivants se décalent. */
  const resize = (t: TaskWithJob, run: number | null) => {
    const m = machines.find((x) => x.id === t.machine_id);
    if (!m || !t.planned_start) return;
    place(t, m, new Date(t.planned_start), { lock: t.locked, manual: run, okText: run === null ? "Durée recalculée d'après la cadence" : `Production de ${t.job.of_number} : ${fmtDuration(run)}` });
  };

  const sel = tasks.find((t) => t.id === selected) || null;

  return (
    <div className="page page-planning">
      <Header title="Planning">
        <div className="seg">
          <button className={view === "jour" ? "on" : ""} onClick={() => setView("jour")}>Jour</button>
          <button className={view === "semaine" ? "on" : ""} onClick={() => setView("semaine")}>Semaine</button>
        </div>
        <div className="seg">
          <button aria-label="Précédent" onClick={() => setAnchor(addDays(anchor, view === "jour" ? -1 : -7))}>‹</button>
          <button onClick={() => setAnchor(startOfDay(new Date()))}>Aujourd'hui</button>
          <button aria-label="Suivant" onClick={() => setAnchor(addDays(anchor, view === "jour" ? 1 : 7))}>›</button>
        </div>
        <span className="period">{view === "jour" ? longDay(anchor) : `Semaine du ${fmtDate(startOfWeek(anchor))}`}</span>
        <span className="spacer" />
        {alerts.length > 0 && <button className={`btn btn-warn ${showAlerts ? "on" : ""}`} onClick={() => setShowAlerts(!showAlerts)} aria-expanded={showAlerts}><span className="warn-dot" aria-hidden="true" />{alerts.length} alerte{alerts.length > 1 ? "s" : ""}</button>}
        {edit ? <>
          <button className="btn btn-primary" onClick={runOptimize}>Optimiser</button>
          <button className={`btn btn-dark ${toPublish ? "has-changes" : ""}`} onClick={publish} disabled={!toPublish} title={toPublish ? "Envoie le planning aux tablettes des postes" : "Les tablettes ont le dernier planning"}>
            {toPublish ? <>Publier<span className="pub-count">{toPublish}</span></> : <><Check size={15} /> Publié</>}
          </button>
        </> : <span className="badge">Lecture seule</span>}
      </Header>

      {showAlerts && (
        <div className="alerts">
          {alerts.map((a, i) => {
            const t = tasks.find((x) => x.id === a.task_id)!;
            return <button key={i} className="alert-row" onClick={() => setSelected(t.id)}><b>{t.job.of_number}</b><span>{t.job.client}</span><em>{a.message}</em></button>;
          })}
        </div>
      )}

      <div className="planning">
        <Backlog tasks={backlog} onSelect={setSelected} onPlace={place} machines={machines} edit={edit} canAdd={can("jobs")} />
        <Timeline
          days={days} hours={hours} machines={machines} tasks={tasks} now={now} alertIds={alertIds}
          onSelect={setSelected} onPlace={place} onResize={resize} selected={selected} edit={edit} animate={animate} operators={data.operators}
        />
      </div>

      {sel && <TaskDrawer t={sel} machines={machines} operators={data.operators} edit={edit} onClose={() => setSelected(null)} onPlace={place} onResize={resize}
        onAssign={(opId) => save([{ task_id: sel.id, machine_id: sel.machine_id, planned_start: sel.planned_start, planned_end: sel.planned_end, setup_min: sel.setup_min, run_min: sel.run_min, operator_id: opId }], opId ? `Attribué à ${data.operators.find((o) => o.id === opId)?.name}` : "Attribution retirée")}
        onLock={(v) => save([{ task_id: sel.id, machine_id: sel.machine_id, planned_start: sel.planned_start, planned_end: sel.planned_end, setup_min: sel.setup_min, run_min: sel.run_min, locked: v }], v ? "Verrouillé : l'optimisation ne le déplacera plus" : "Déverrouillé")}
        alerts={alerts.filter((a) => a.task_id === sel.id).map((a) => a.message)} />}

      {plan && <OptimizeModal plan={plan} tasks={tasks} machines={machines} onClose={() => setPlan(null)} onApply={applyPlan} />}
    </div>
  );
}

function longDay(d: Date): string {
  const t = startOfDay(new Date()).getTime(), x = startOfDay(d).getTime();
  const rel = x === t ? "Aujourd'hui · " : x === addDays(new Date(t), 1).getTime() ? "Demain · " : "";
  const s = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  return rel + (rel ? s : s.charAt(0).toUpperCase() + s.slice(1));
}

export function Header({ title, children }: { title: string; children?: ReactNode }) {
  return <div className="page-head"><h1>{title}</h1>{children}</div>;
}

// ---------------------------------------------------------------------------
// Glisser-déposer (souris, doigt et stylet)
// ---------------------------------------------------------------------------
interface DragState { task: TaskWithJob; offsetX: number; x: number; y: number; moved: boolean; startX: number; startY: number }
type PlaceFn = (t: TaskWithJob, m: Machine | null, at: Date | null) => void;

let dragListener: ((d: DragState | null) => void) | null = null;
type Preview = { start: Date; end: Date; setup: number; machine: string };
/** Fourni par la page : où atterrirait l'étape (null = lecture seule). */
let previewPlace: ((t: TaskWithJob, machineId: string, at: Date) => Preview | null) | null = null;
type LaneEl = HTMLElement & { timeAt?: (x: number) => Date; xOf?: (d: Date) => number | null };

function startDrag(e: RPointerEvent, task: TaskWithJob, onClick: () => void, onDrop: (d: DragState, target: Element | null) => void, enabled = true) {
  if (e.button !== 0) return;
  if (!enabled) { onClick(); return; }
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const st: DragState = { task, offsetX: e.clientX - rect.left, x: e.clientX, y: e.clientY, moved: false, startX: e.clientX, startY: e.clientY };
  const move = (ev: PointerEvent) => {
    st.x = ev.clientX; st.y = ev.clientY;
    if (!st.moved && Math.hypot(st.x - st.startX, st.y - st.startY) > 5) st.moved = true;
    if (st.moved) { ev.preventDefault(); dragListener?.({ ...st }); }
  };
  const up = (ev: PointerEvent) => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
    window.removeEventListener("pointercancel", up);
    dragListener?.(null);
    if (!st.moved) { onClick(); return; }
    const el = document.elementFromPoint(ev.clientX, ev.clientY);
    onDrop(st, el);
  };
  window.addEventListener("pointermove", move, { passive: false });
  window.addEventListener("pointerup", up);
  window.addEventListener("pointercancel", up);
}

function DragGhost() {
  const [d, setD] = useState<DragState | null>(null);
  useEffect(() => { dragListener = setD; return () => { dragListener = null; }; }, []);
  let lane: LaneEl | null = null;
  if (d) lane = (document.elementFromPoint(d.x, d.y)?.closest("[data-lane]") as LaneEl | null) || null;
  useEffect(() => {
    document.body.classList.toggle("dragging", !!d);
    document.querySelectorAll(".lane.hover, .backlog.hover").forEach((x) => x.classList.remove("hover"));
    if (d) document.elementFromPoint(d.x, d.y)?.closest(".lane, .backlog")?.classList.add("hover");
  }, [d]);
  if (!d) return null;
  // aperçu exact : l'emplacement réel (après les jobs déjà placés), avec les heures
  let pv: Preview | null = null, box: { left: number; top: number; width: number; height: number } | null = null;
  if (lane && lane.timeAt && lane.xOf && previewPlace) {
    const r = lane.getBoundingClientRect();
    pv = previewPlace(d.task, lane.dataset.lane!, lane.timeAt(d.x - d.offsetX - r.left));
    const x0 = pv ? lane.xOf(pv.start) : null;
    if (pv && x0 !== null) {
      const x1 = lane.xOf(pv.end);
      const sameDay = pv.end.toDateString() === pv.start.toDateString();
      const w = sameDay && x1 !== null ? x1 - x0 : r.width - x0;
      box = { left: r.left + x0, top: r.top + 9, width: Math.max(6, Math.min(w, r.right - (r.left + x0))), height: 68 };
    }
  }
  return createPortal(
    <>
      {box && pv && (
        <div className="drop-preview" style={{ left: box.left, top: box.top, width: box.width, height: box.height, ["--fc" as string]: formatColor(d.task.format_out) }}>
          {pv.setup > 0 && <span className="dp-setup" style={{ width: `${Math.min(100, (pv.setup / Math.max(1, (pv.end.getTime() - pv.start.getTime()) / 60000)) * 100)}%` }} />}
        </div>
      )}
      <div className="ghost" style={{ left: box ? Math.min(box.left, window.innerWidth - 300) : d.x - d.offsetX, top: box ? box.top + box.height + 8 : d.y - 18, borderLeftColor: formatColor(d.task.format_out) }}>
        <span><b>{d.task.job.of_number}</b> {d.task.job.client}</span>
        {pv ? <span className="ghost-time">{pv.machine} · {fmtTime(pv.start)} → {fmtTime(pv.end)}{pv.end.toDateString() !== pv.start.toDateString() ? ` (${fmtDate(pv.end)})` : ""}{pv.setup ? ` · calage ${fmtDuration(pv.setup)}` : " · même réglage"}</span>
          : lane === null && <span className="ghost-time">Déposez sur une machine</span>}
      </div>
    </>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Jobs à planifier
// ---------------------------------------------------------------------------
function Backlog({ tasks, onSelect, onPlace, machines, edit, canAdd }: { tasks: TaskWithJob[]; onSelect: (id: string) => void; onPlace: PlaceFn; machines: Machine[]; edit: boolean; canAdd: boolean }) {
  return (
    <section className="backlog" data-backlog="1">
      <div className="backlog-head">
        <div>
          <h2>À planifier <span className="count-pill">{tasks.length}</span></h2>
          {edit && tasks.length > 0 && <p>Glissez un job sur une machine, ou lancez « Optimiser ».</p>}
        </div>
        {canAdd && <a href="#/jobs/nouveau" className="btn btn-small">+ Job</a>}
      </div>
      {!tasks.length && <div className="backlog-empty"><b>Tout est planifié</b><span>Les nouveaux jobs arrivent ici.</span></div>}
      <div className="backlog-list">
        {tasks.map((t) => {
          const due = dueInfo(t.job.due_at);
          const sheets = t.job.sheets_ready_at && new Date(t.job.sheets_ready_at) > new Date();
          const compatible = machines.some((m) => isCompatible(m, t));
          return (
            <div key={t.id} className={`card-job ${edit ? "" : "ro"}`} style={{ ["--fc" as string]: formatColor(t.format_out) }}
              onPointerDown={(e) => startDrag(e, t, () => onSelect(t.id), (d, el) => dropOn(d, el, onPlace, machines), edit)}>
              <div className="cj-top">
                <b className="cj-of">{t.job.of_number}</b>
                {t.job.priority === "urgent" && <Badge tone="red">Urgent</Badge>}
                <span className={`due due-${due.tone}`}>{due.label}</span>
              </div>
              <div className="cj-title">{t.job.client}</div>
              <div className="cj-sub">{t.job.title}</div>
              <div className="cj-meta">
                <span className="cj-fmt" style={{ background: formatColor(t.format_out) }}>{t.format_in} → {t.format_out}</span>
                <span>{OPERATION_LABEL[t.operation]}{t.fold_type ? ` ${t.fold_type}` : ""}</span>
                <span>{t.job.quantity.toLocaleString("fr-FR")} ex.</span>
              </div>
              {paramSummary(t).length > 0 && <div className="cj-params">{paramSummary(t).map((p) => p.value).join(" · ")}</div>}
              {sheets && <div className="cj-warn">Feuilles disponibles {fmtDateTime(t.job.sheets_ready_at)}</div>}
              {!compatible && <div className="cj-warn">Aucune machine compatible</div>}
            </div>
          );
        })}
      </div>
      <DragGhost />
    </section>
  );
}

/** Échéance lisible : « En retard », « Aujourd'hui 16:00 », « Demain », sinon la date. */
export function dueInfo(iso: string, now = new Date()): { label: string; tone: "late" | "soon" | "" } {
  const d = new Date(iso);
  const days = Math.round((startOfDay(d).getTime() - startOfDay(now).getTime()) / 86_400_000);
  if (d < now) return { label: "En retard", tone: "late" };
  if (days === 0) return { label: `Aujourd'hui ${fmtTime(d)}`, tone: "soon" };
  if (days === 1) return { label: `Demain ${fmtTime(d)}`, tone: "soon" };
  return { label: fmtDate(d), tone: "" };
}

function dropOn(d: DragState, el: Element | null, onPlace: PlaceFn, machines: Machine[]) {
  if (!el) return;
  if (el.closest("[data-backlog]")) { if (d.task.machine_id) onPlace(d.task, null, null); return; }
  const lane = el.closest<HTMLElement>("[data-lane]");
  if (!lane) return;
  const m = machines.find((x) => x.id === lane.dataset.lane);
  const toTime = (lane as HTMLElement & { timeAt?: (x: number) => Date }).timeAt;
  if (!m || !toTime) return;
  const rect = lane.getBoundingClientRect();
  onPlace(d.task, m, toTime(d.x - d.offsetX - rect.left));
}

// ---------------------------------------------------------------------------
// Frise par machine
// ---------------------------------------------------------------------------
function Timeline(props: {
  days: Date[]; hours: WorkingHours; machines: Machine[]; tasks: TaskWithJob[]; now: Date; alertIds: Set<string>;
  onSelect: (id: string) => void; onPlace: PlaceFn; onResize: (t: TaskWithJob, run: number | null) => void; selected: string | null; edit: boolean; animate: boolean; operators: Operator[];
}) {
  const { days, hours, machines, tasks, now, alertIds, onSelect, onPlace, onResize, selected, edit, animate, operators } = props;
  const [rz, setRz] = useState<{ id: string; end: Date } | null>(null);
  const opName = (id: string | null) => (id ? operators.find((o) => o.id === id)?.name || null : null);
  const wrap = useRef<HTMLDivElement>(null);
  const [cw, setCw] = useState(1100);
  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setCw(el.clientWidth));
    ro.observe(el);
    setCw(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  // sur téléphone, la colonne des machines se fait plus étroite
  const LW = cw < 640 ? 116 : LABEL_W;
  const width = Math.max(320, cw - LW);
  const [w0, w1] = dayWindow(hours);
  const span = Math.max(60, w1 - w0);
  const minDayW = days.length > 1 ? 150 : cw < 640 ? 760 : 600;
  const dayW = Math.max(minDayW, width / days.length);
  const total = dayW * days.length;

  const xOf = (t: Date): number | null => {
    const i = days.findIndex((d) => d.toDateString() === t.toDateString());
    if (i < 0) return null;
    const m = t.getHours() * 60 + t.getMinutes();
    return i * dayW + (Math.min(Math.max(m - w0, 0), span) / span) * dayW;
  };
  const timeAt = (x: number): Date => {
    const i = Math.min(days.length - 1, Math.max(0, Math.floor(x / dayW)));
    const frac = Math.min(1, Math.max(0, (x - i * dayW) / dayW));
    const mins = Math.round((w0 + frac * span) / SNAP_MIN) * SNAP_MIN;
    const d = new Date(days[i]); d.setHours(0, mins, 0, 0);
    return d;
  };
  const pieces = (a: Date, b: Date) => {
    const out: { left: number; width: number }[] = [];
    days.forEach((d, i) => {
      const s = new Date(d); s.setHours(0, w0, 0, 0);
      const e = new Date(d); e.setHours(0, w1, 0, 0);
      const from = a > s ? a : s, to = b < e ? b : e;
      if (to > from) {
        const l = xOf(from)!, r = i * dayW + ((to.getHours() * 60 + to.getMinutes() - w0) / span) * dayW;
        out.push({ left: l, width: Math.max(4, r - l) });
      }
    });
    return out;
  };
  const hoursTicks: number[] = [];
  for (let h = Math.ceil(w0 / 60); h * 60 < w1 || (days.length === 1 && h * 60 === w1); h++) hoursTicks.push(h);
  const breaks: [number, number][] = [];
  const sortedRanges = [...hours.ranges].sort((a, b) => a.start.localeCompare(b.start));
  for (let i = 1; i < sortedRanges.length; i++) {
    const toMin = (s: string) => { const [h, m] = s.split(":").map(Number); return h * 60 + (m || 0); };
    breaks.push([toMin(sortedRanges[i - 1].end), toMin(sortedRanges[i].start)]);
  }
  const nowX = xOf(now);
  const grid: number[] = [];
  for (let g = Math.ceil(w0 / 30) * 30; g < w1; g += 30) if (g > w0) grid.push(g);
  /** Tirer le bord droit d'un bloc : nouvelle fin, donc nouvelle durée de production. */
  const startResize = (ev: RPointerEvent, t: TaskWithJob, laneEl: HTMLElement) => {
    ev.stopPropagation(); ev.preventDefault();
    const s = new Date(t.planned_start!);
    const minEnd = addWorkingMinutes(s, (t.setup_min || 0) + 5, hours);
    let end = new Date(t.planned_end || s);
    const move = (e: PointerEvent) => {
      const r = laneEl.getBoundingClientRect();
      const at = timeAt(e.clientX - r.left);
      end = at < minEnd ? minEnd : at;
      setRz({ id: t.id, end });
    };
    const up = () => {
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up);
      document.body.classList.remove("resizing");
      setRz(null);
      const run = Math.max(5, workingMinutesBetween(s, end, hours) - (t.setup_min || 0));
      if (run !== t.run_min) onResize(t, run);
    };
    document.body.classList.add("resizing");
    setRz({ id: t.id, end });
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up); window.addEventListener("pointercancel", up);
  };

  return (
    <section className={`timeline ${animate ? "tl-animate" : ""}`} ref={wrap}>
      <div className="tl-scroll">
        <div className="tl-inner" style={{ width: total + LW }}>
          <div className="tl-head" style={{ paddingLeft: LW }}>
            {nowX !== null && <div className="now-tag" style={{ left: LW + nowX }}>{fmtTime(now)}</div>}
            {days.map((d, i) => (
              <div key={i} className={`tl-day ${d.toDateString() === now.toDateString() ? "today" : ""}`} style={{ width: dayW }}>
                <div className="tl-day-name">{fmtDate(d)}</div>
                <div className="tl-hours">
                  {hoursTicks.map((h) => (
                    <span key={h} style={{ left: ((h * 60 - w0) / span) * dayW }}>{(days.length > 3 && h % 2) || (i > 0 && h * 60 - w0 < 60) ? "" : `${h}h`}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {machines.map((m, mi) => {
            const lane = tasks.filter((t) => t.machine_id === m.id && t.planned_start);
            const load = lane.filter((t) => t.status !== "termine").reduce((a, t) => a + (t.setup_min || 0) + (t.run_min || 0), 0);
            return (
              <div key={m.id} className="tl-row">
                <div className="tl-label" style={{ width: LW }}>
                  <b>{m.name}</b>
                  <span>{load ? `${fmtDuration(load)} à venir` : "Rien à venir"}</span>
                  {lane.some((t) => isActive(t)) && <span className="tl-live">En cours</span>}
                </div>
                <div className="lane" data-lane={m.id} style={{ width: total }}
                  ref={(el) => { if (el) { (el as LaneEl).timeAt = timeAt; (el as LaneEl).xOf = xOf; } }}>
                  {days.map((d, i) => (
                    <div key={i} className={`lane-day ${dayRanges(d, hours).length ? "" : "off"}`} style={{ left: i * dayW, width: dayW }}>
                      {days.length === 1 && grid.map((g) => <i key={g} className={`lane-tick ${g % 60 ? "half" : ""}`} style={{ left: ((g - w0) / span) * dayW }} />)}
                      {breaks.map(([a, b], k) => <div key={k} className="lane-break" style={{ left: ((a - w0) / span) * dayW, width: ((b - a) / span) * dayW }} />)}
                    </div>
                  ))}
                  {lane.map((t) => {
                    const s = new Date(t.planned_start!), e = rz?.id === t.id ? rz.end : new Date(t.planned_end || t.planned_start!);
                    const setupEnd = t.setup_min ? addWorkingMinutes(s, t.setup_min, hours) : s;
                    const ps = pieces(s, e);
                    const hatch = pieces(s, setupEnd);
                    const cls = ["block", `st-${t.status}`, alertIds.has(t.id) ? "has-alert" : "", selected === t.id ? "selected" : "", rz?.id === t.id ? "resizing" : "", t.manual_run_min ? "manual" : ""].join(" ");
                    const canResize = edit && !isActive(t) && t.status !== "termine";
                    const who = opName(t.operator_id);
                    return ps.map((p, k) => (
                      <div key={t.id + k} className={cls} style={{ left: p.left, width: p.width, background: formatColor(t.format_out), ["--i" as string]: mi }}
                        title={`${t.job.of_number} · ${t.job.client} — ${t.job.title}\n${OPERATION_LABEL[t.operation]} ${t.format_in} → ${t.format_out}\n${fmtTime(s)} → ${fmtTime(e)} · calage ${fmtDuration(t.setup_min)} · production ${fmtDuration(t.run_min)}`}
                        onPointerDown={(ev) => startDrag(ev, t, () => onSelect(t.id), (d, el) => dropOn(d, el, onPlace, machines), edit)}>
                        {k === 0 && hatch[0] && <span className="hatch" style={{ width: Math.min(hatch[0].width, p.width) }} />}
                        {k === 0 && p.width > 44 && (
                          <span className="block-text">
                            <span className="bt-1">{t.locked && "🔒 "}<b>{t.job.of_number}</b> {t.job.client}</span>
                            <span className="bt-2">{t.format_in} → {t.format_out} · {t.job.quantity.toLocaleString("fr-FR")} ex.</span>
                            {p.width > 120 && <span className="bt-3">{fmtTime(s)} – {fmtTime(e)}{who ? ` · ${who}` : ""}</span>}
                          </span>
                        )}
                        {k === 0 && who && p.width > 30 && p.width <= 120 && <span className="block-who" title={`Conducteur : ${who}`}>{who.slice(0, 1)}</span>}
                        {k === 0 && p.width <= 44 && t.locked && <span className="block-text">🔒</span>}
                        {k === ps.length - 1 && canResize && (
                          <span className="block-resize" title="Tirer pour changer la durée"
                            onPointerDown={(ev) => startResize(ev, t, (ev.currentTarget as HTMLElement).closest("[data-lane]") as HTMLElement)} />
                        )}
                        {k === ps.length - 1 && rz?.id === t.id && <span className="rz-tag">fin {fmtTime(rz.end)} · {fmtDuration(Math.max(5, workingMinutesBetween(s, rz.end, hours) - (t.setup_min || 0)))} de production</span>}
                      </div>
                    ));
                  })}
                  {nowX !== null && <div className="now-line" style={{ left: nowX }} />}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="legend">
        <span><i className="lg-hatch" /> calage</span>
        <span><i className="lg-run" /> production (couleur = format de sortie)</span>
        <span>🔒 verrouillé</span>
        <span className="muted">{edit ? "Glissez un job sur une machine : l'heure exacte s'affiche avant de lâcher. Tirez le bord droit d'un bloc pour changer sa durée." : "Cliquez sur un job pour voir son détail."}</span>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Détail d'une étape
// ---------------------------------------------------------------------------
function TaskDrawer({ t, machines, operators, edit, alerts, onClose, onPlace, onResize, onLock, onAssign }: {
  t: TaskWithJob; machines: Machine[]; operators: Operator[]; edit: boolean; alerts: string[]; onClose: () => void; onPlace: PlaceFn;
  onResize: (t: TaskWithJob, run: number | null) => void; onLock: (v: boolean) => void; onAssign: (operatorId: string | null) => void;
}) {
  const hhmm = (iso: string | null) => (iso ? fmtTime(iso) : "");
  const [startDraft, setStartDraft] = useState(hhmm(t.planned_start));
  useEffect(() => { setStartDraft(hhmm(t.planned_start)); }, [t.planned_start]);
  useEffect(() => {
    const f = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", f);
    return () => window.removeEventListener("keydown", f);
  }, [onClose]);
  const eligible = operators.filter((o) => o.role !== "conducteur" || !o.machine_ids.length || !t.machine_id || o.machine_ids.includes(t.machine_id));
  const m = machines.find((x) => x.id === t.machine_id);
  const statusLabel: Record<string, string> = { a_planifier: "À planifier", planifie: "Planifié", calage: "En calage", production: "En production", pause: "En pause", termine: "Terminé" };
  const compatible = machines.filter((x) => isCompatible(x, t));
  const planEditable = edit && !isActive(t) && t.status !== "termine" && !!m && !!t.planned_start;
  const run = t.run_min ?? 0;
  const computed = m ? runMinutes({ ...t, manual_run_min: null }, m) : run;
  const moveTo = () => {
    if (!m || !t.planned_start || !/^\d{1,2}:\d{2}$/.test(startDraft)) return;
    const [h, mi] = startDraft.split(":").map(Number);
    const d = new Date(t.planned_start); d.setHours(h, mi, 0, 0);
    onPlace(t, m, d);
  };
  return (
    <aside className="drawer" role="dialog" aria-label={t.job.of_number}>
      <div className="drawer-head">
        <div><h2>{t.job.of_number} · {t.job.client}</h2><p className="muted">{t.job.title}</p></div>
        <button className="icon-btn" onClick={onClose} aria-label="Fermer">✕</button>
      </div>
      <div className="row-gap"><Badge tone={t.status === "termine" ? "" : isActive(t) ? "teal" : "blue"}>{statusLabel[t.status]}</Badge>{t.locked && <Badge>🔒 Verrouillé</Badge>}{t.job.priority === "urgent" && <Badge tone="red">Urgent</Badge>}</div>
      {alerts.map((a, i) => <div key={i} className="cj-warn">⚠ {a}</div>)}
      <dl className="facts">
        <dt>Opération</dt><dd>{OPERATION_LABEL[t.operation]}{t.fold_type ? ` · pli ${t.fold_type}` : ""}</dd>
        <dt>Format</dt><dd>{t.format_in} → {t.format_out}</dd>
        <dt>Papier</dt><dd>{t.paper || "—"}{t.grammage ? ` · ${t.grammage} g` : ""}{t.pages ? ` · ${t.pages} p.` : ""}</dd>
        {paramSummary(t).map((p) => <Fragment key={p.label}><dt>{p.label}</dt><dd>{p.value === p.label ? "Oui" : p.value}</dd></Fragment>)}
        {t.packaging && <><dt>Conditionnement</dt><dd>{t.packaging}</dd></>}
        <dt>Quantité</dt><dd>{t.job.quantity.toLocaleString("fr-FR")} ex.{t.produced_qty ? ` · ${t.produced_qty.toLocaleString("fr-FR")} faits` : ""}</dd>
        <dt>Échéance</dt><dd>{fmtDateTime(t.job.due_at)}</dd>
        {t.job.sheets_ready_at && <><dt>Feuilles</dt><dd>{fmtDateTime(t.job.sheets_ready_at)}</dd></>}
        <dt>Machine</dt><dd>{m ? m.name : "Non planifié"}</dd>
        {t.planned_start && <><dt>Prévu</dt><dd>{fmtDateTime(t.planned_start)} → {fmtTime(t.planned_end)}</dd></>}
        {t.planned_start && <><dt>Durée</dt><dd>calage {fmtDuration(t.setup_min)} · production {fmtDuration(t.run_min)}</dd></>}
      </dl>
      {planEditable && (
        <div className="dr-plan">
          <div className="dr-row">
            <label className="field dr-start">
              <span className="field-label">Début</span>
              <input type="time" step={300} value={startDraft} onChange={(e) => setStartDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") moveTo(); }} />
            </label>
            <button className="btn" onClick={moveTo} disabled={startDraft === hhmm(t.planned_start)}>Déplacer</button>
            <span className="dr-end">Fin prévue <b>{fmtTime(t.planned_end)}</b></span>
          </div>
          <div className="dur-edit">
            <span className="field-label">Durée de production</span>
            <div className="dur-row">
              <button className="btn btn-small" onClick={() => onResize(t, Math.max(5, run - 15))} disabled={run <= 5} aria-label="Moins 15 minutes">−15</button>
              <button className="btn btn-small" onClick={() => onResize(t, Math.max(5, run - 5))} disabled={run <= 5} aria-label="Moins 5 minutes">−5</button>
              <b>{fmtDuration(run)}</b>
              <button className="btn btn-small" onClick={() => onResize(t, run + 5)} aria-label="Plus 5 minutes">+5</button>
              <button className="btn btn-small" onClick={() => onResize(t, run + 15)} aria-label="Plus 15 minutes">+15</button>
            </div>
            <span className="field-hint">
              {t.manual_run_min
                ? <>Fixée à la main (calcul d'après la cadence : {fmtDuration(computed)}). <button className="link" onClick={() => onResize(t, null)}>Revenir au calcul</button></>
                : <>Calculée d'après la cadence de {m!.name}. Vous pouvez aussi tirer le bord droit du bloc.</>}
              {t.setup_min ? ` Calage en plus : ${fmtDuration(t.setup_min)}.` : " Pas de calage (même réglage que le job d'avant)."}
            </span>
          </div>
        </div>
      )}
      {t.job.notes && <div className="notes"><b>Consignes</b><p>{t.job.notes}</p></div>}
      {t.status !== "termine" && (
        <label className="field">
          <span className="field-label">Conducteur attribué</span>
          <select disabled={!edit} value={t.operator_id || ""} onChange={(e) => onAssign(e.target.value || null)}>
            <option value="">Aucun : le conducteur au poste</option>
            {eligible.map((o) => <option key={o.id} value={o.id}>{o.name} · {OPERATOR_ROLE_LABEL[o.role]}</option>)}
          </select>
          <span className="field-hint">Sur la tablette, un autre conducteur ne pourra pas démarrer ce job.</span>
        </label>
      )}
      {edit && !isActive(t) && t.status !== "termine" && (
        <div className="stack">
          {t.machine_id && <button className="btn" onClick={() => onLock(!t.locked)}>{t.locked ? "Déverrouiller" : "Verrouiller à cet horaire"}</button>}
          {!t.machine_id && compatible.length > 0 && (
            <div className="stack-tight">
              <span className="field-label">Placer au plus tôt sur</span>
              {compatible.map((x) => <button key={x.id} className="btn" onClick={() => onPlace(t, x, new Date())}>{x.name}</button>)}
            </div>
          )}
          {t.machine_id && <button className="btn" onClick={() => { onPlace(t, null, null); onClose(); }}>Retirer du planning</button>}
        </div>
      )}
      <a className="btn btn-ghost" href={`#/jobs/${t.job_id}`} onClick={() => navigate(`jobs/${t.job_id}`)}>Ouvrir la fiche du job</a>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Optimisation : avant / après
// ---------------------------------------------------------------------------
function OptimizeModal({ plan, tasks, machines, onClose, onApply }: { plan: PlanResult; tasks: TaskWithJob[]; machines: Machine[]; onClose: () => void; onApply: () => void }) {
  const b = plan.baseline, a = plan.metrics;
  const saved = b.setup_min - a.setup_min;
  const avoided = b.recalages - a.recalages;
  const lateFixed = b.late.length - a.late.length;
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const name = (id: string) => { const t = byId.get(id); return t ? `${t.job.of_number} ${t.job.client}` : id; };
  const perMachine = machines.map((m) => ({ m, n: plan.slots.filter((s) => s.machine_id === m.id).length })).filter((x) => x.n);
  return (
    <Modal title="Optimiser le planning" onClose={onClose} wide>
      {!plan.slots.length ? <p>Aucun job à planifier pour l'instant.</p> : (
        <>
          <div className={`opt-hero ${avoided > 0 || saved > 0 ? "good" : ""}`}>
            <b>{avoided > 0 ? `${avoided} calage${avoided > 1 ? "s" : ""} évité${avoided > 1 ? "s" : ""}` : saved > 0 ? `${fmtDuration(saved)} de calage en moins` : "Votre ordre actuel est déjà bon"}</b>
            <span>{saved > 0 ? `soit ${fmtDuration(saved)} de machine rendues à la production` : "Ordopli n'a pas trouvé de regroupement plus efficace."}{lateFixed > 0 ? ` · ${lateFixed} retard${lateFixed > 1 ? "s" : ""} rattrapé${lateFixed > 1 ? "s" : ""}` : ""}</span>
          </div>
          <div className="compare">
            <div><span>Calages</span><b className="before">{b.recalages}</b><b>→ {a.recalages}</b></div>
            <div><span>Temps de calage</span><b className="before">{fmtDuration(b.setup_min)}</b><b>→ {fmtDuration(a.setup_min)}</b></div>
            <div><span>Jobs en retard</span><b className="before">{b.late.length}</b><b className={a.late.length ? "bad" : ""}>→ {a.late.length}</b></div>
          </div>
          <p className="small muted">Ordopli regroupe les jobs par réglage (opération, pli, formats) sans dépasser les échéances. Les jobs verrouillés et ceux en cours ne bougent pas. {plan.slots.length} étapes réparties : {perMachine.map((x) => `${x.m.name} (${x.n})`).join(", ")}.</p>
          {a.late.length > 0 && <div className="cj-warn">Toujours en retard : {a.late.map(name).join(", ")}. Il manque de la capacité machine avant leur échéance.</div>}
        </>
      )}
      {plan.unplaced.length > 0 && <div className="cj-warn">Non planifiables : {plan.unplaced.map((u) => `${name(u.task_id)} (${u.reason.toLowerCase()})`).join(", ")}.</div>}
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Annuler</button>
        <button className="btn btn-primary" onClick={onApply} disabled={!plan.slots.length}>Appliquer ce planning</button>
      </div>
    </Modal>
  );
}
